import React, { useState, useEffect, useCallback } from 'react';
import './Dashboard.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const growthBadge = (value) => {
  const v = Number(value) || 0;
  const cls = v >= 0 ? 'pos' : 'neg';
  return <span className={`growth-badge ${cls}`}>{v >= 0 ? '+' : ''}{v.toFixed(2)}%</span>;
};

// Simple vertical bar chart (no external chart library).
const BarChart = ({ data, valueKey, label }) => {
  const values = (data || []).map(d => Number(d[valueKey]) || 0);
  const max = Math.max(1, ...values);
  return (
    <div className="chart-wrap">
      <div className="chart-label">{label} (last {values.length} days)</div>
      <div className="chart-bars">
        {(data || []).map((d, i) => {
          const h = Math.round((values[i] / max) * 100);
          return (
            <div className="chart-col" key={d.date} title={`${d.date}: ${values[i]}`}>
              <div className="chart-bar" style={{ height: `${h}%` }} />
              <span className="chart-day">{d.date.slice(5)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Dashboard = ({ global = false, onDataChange }) => {
  const [from, setFrom] = useState(fmtDate(new Date(Date.now() - 29 * 86400000)));
  const [to, setTo] = useState(fmtDate(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState('');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const endpoint = global ? '/api/super-admin/dashboard/analytics' : '/api/admin/dashboard/analytics';
      let url = `${API_BASE}${endpoint}?from=${from}&to=${to}`;
      if (global && selectedCountryId) url += `&countryId=${selectedCountryId}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        setData(json.data);
        if (onDataChange) onDataChange(json.data);
      }
      else setError(json.message || 'Failed to load analytics');
    } catch (e) {
      setError(e.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [from, to, global, selectedCountryId]);

  useEffect(() => {
    if (!global) return;
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/super-admin/countries?getAll=true`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(j => { if (j.success) setCountries(j.countries || []); })
      .catch(() => {});
  }, [global]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const a = data?.activeUsers;
  const u = data?.usage;

  return (
    <div className="dash-page">
      <div className="dash-header">
        {!global && <h2>📊 Dashboard</h2>}
        <div className="dash-period">
          <label>From <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
          <label>To <input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
          <button onClick={() => { setFrom(fmtDate(new Date(Date.now() - 29 * 86400000))); setTo(fmtDate(new Date())); }}>
            Last 30 days
          </button>
          {global && (
            <label className="dash-country">
              Country
              <select value={selectedCountryId} onChange={e => setSelectedCountryId(e.target.value)}>
                <option value="">🌍 Global (All)</option>
                {countries.map(c => (
                  <option key={c.id} value={c.id}>{c.country_name}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>

      {loading && <div className="dash-status">Loading…</div>}
      {error && <div className="dash-status dash-error">{error}</div>}

      {/* Active Customers */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h3>👥 Total Active Customers</h3>
          <div className="dash-growth">
            <span>DoD {growthBadge(a?.dod)}</span>
            <span>MoM {growthBadge(a?.mom)}</span>
            <span>YoY {growthBadge(a?.yoy)}</span>
          </div>
        </div>
        <div className="dash-total">{a?.total ?? 0}</div>
        <BarChart data={a?.daily} valueKey="count" label="Daily active users" />
      </div>

      {/* Customer Usage */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h3>💰 Customer Usage</h3>
          <div className="dash-growth">
            <span>DoD {growthBadge(u?.dod)}</span>
            <span>MoM {growthBadge(u?.mom)}</span>
            <span>YoY {growthBadge(u?.yoy)}</span>
          </div>
        </div>
        <div className="dash-total">${(u?.total ?? 0).toFixed(2)}</div>
        <BarChart data={u?.daily} valueKey="amount" label="Daily usage (USD)" />
      </div>

      {/* Total Registered Customers + Liability (combined) */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h3>👥 Customers & 💰 Liability</h3>
        </div>
        <div className="dash-dual">
          <div className="dash-dual-item">
            <span className="dash-dual-label">Total Registered Customers</span>
            <span className="dash-dual-value">{data?.totalRegisteredCustomers ?? 0}</span>
          </div>
          <div className="dash-dual-item">
            <span className="dash-dual-label">Wallet Balance (Liability)</span>
            <span className="dash-dual-value">${(data?.totalWalletBalance ?? 0).toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
